import {KeyValueDto} from '../../repositories/dto/key-value.dto';
import {NavItem} from "./nav.item";
import {Paths} from "../../paths";
import {isDefined, notDefined} from "../../libs/utils/epic.utils";
import {Routes} from "@angular/router";

/** Alle Seitenpfade, die der Router kennt, samt verschachtelter Kinder - ohne Umleitungen und Platzhalter. */
export function collectRoutePaths(routes: Routes, prefix: string = ''): Set<string> {
  const paths: Set<string> = new Set<string>();
  routes.forEach((route) => {
    const path: string = [prefix, route.path ?? ''].filter((segment) => segment !== '').join('/');
    if (isDefined(route.children)) {
      collectRoutePaths(route.children, path).forEach((child) => paths.add(child));
    } else if (notDefined(route.redirectTo) && route.path !== '**') {
      paths.add(path);
    }
  });
  return paths;
}

export class NavTree {

  private readonly items: NavItem[] = [];
  private selected: NavItem | undefined;

  public constructor(items: NavItem[]) {
    this.items = items;
  }

  public getItems(): NavItem[] {
    return this.items;
  }

  public getByPath(path: string, params: KeyValueDto[]): NavItem | undefined {
    const founds = this.findByPath(path, params, this.getItems());
    if (founds && founds.length > 0) {
      return founds
        .sort((a, b) => b.paramCount - a.paramCount)
        .map((f) => f.item)
        .shift();
    }
    return undefined;
  }

  public getById(id: string | undefined): NavItem | undefined {
    return id !== undefined ? this.findById(this.getItems(), id) : undefined;
  }

  public select(item: NavItem): void {
    this.deselectAll();
    item.selected = true;
    this.selectParents(item);
    this.selected = item;
  }

  public deselectAll(): void {
    this.deselectItems(this.getItems());
  }

  /**
   * Attention: recursive function
   */
  public deselectItems(items: NavItem[] | undefined): void {
    this.selected = undefined;
    if (items) {
      for (const item of items) {
        item.selected = false;
        item.selectedChild = false;
        this.deselectItems(item.children);
      }
    }
  }

  public getSelected(): NavItem | undefined {
    return this.selected;
  }

  // noinspection JSUnusedGlobalSymbols
  public getOverview(): NavItem {
    return this.getItems().filter((i) => i.id === 'overview-main')[0];
  }

  /**
   * Behaelt nur Eintraege, deren Seite es gibt - fuer den Viewer, der nur einen Teil der Routen mitbringt. Ein Eltern-
   * eintrag bleibt, solange er selbst eine Seite hat oder ein Kind behaelt. Attention: recursive function
   */
  public static retainRouted(items: NavItem[], isRouted: (route: string) => boolean): NavItem[] {
    return items.filter((item) => {
      item.children = NavTree.retainRouted(item.children, isRouted);
      return isRouted(item.route) || item.hasChildren();
    });
  }

  /**
   * Attention: recursive function
   */
  private selectParents(item: NavItem): void {
    if (!item.isSelected()) {
      item.selectedChild = true;
    }
    if (isDefined(item.parent)) {
      this.selectParents(item.parent);
    }
  }

  /**
   * Attention: recursive function
   */
  private findByPath(
    path: string,
    params: KeyValueDto[],
    items: NavItem[],
    found: { item: NavItem; paramCount: number }[] = []): { item: NavItem; paramCount: number }[] | undefined {

    const searchPath = path.indexOf(Paths.PREFIX) == 0 ? path.substring(1) : path;
    for (const item of items) {
      if (item.paths.includes(searchPath)) {
        found.push({item, paramCount: this.countParameter(params, item)});
      }
      if (item.children) {
        this.findByPath(searchPath, params, item.children, found);
      }
    }
    return found;
  }

  /**
   * Attention: recursive function
   */
  private findById(items: NavItem[], id: string): NavItem | undefined {
    for (const item of items) {
      if (item.id === id) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = this.findById(item.children, id);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  private countParameter(params: KeyValueDto[], item: NavItem): number {
    let count = 0;
    item.params.forEach((param) => {
      if (params.some((kv) => param.key === kv.key && param.value === kv.value)) {
        count++;
      }
    });
    return count;
  }
}
